import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, LogOut, Search, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VedLogo from '../../components/VedLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_LABELS = {
  airbnb: 'Airbnb',
  makemytrip: 'MakeMyTrip',
  booking: 'Booking.com',
  direct: 'Direct Booking',
  other: 'Other',
  unknown: 'Unknown',
};

const PLATFORM_COLORS = {
  airbnb: 'bg-red-100 text-red-700 border-red-200',
  makemytrip: 'bg-blue-100 text-blue-700 border-blue-200',
  booking: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  direct: 'bg-purple-100 text-purple-700 border-purple-200',
  other: 'bg-gray-100 text-gray-700 border-gray-200',
  unknown: 'bg-gray-100 text-gray-700 border-gray-200',
};

const StatusBadge = ({ status, source }) => {
  if (status === 'available') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
        <CheckCircle size={12} /> Available
      </span>
    );
  }
  if (status === 'booked_external') {
    const colorClass = PLATFORM_COLORS[source] || PLATFORM_COLORS.unknown;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        <XCircle size={12} /> Booked via {PLATFORM_LABELS[source] || source}
      </span>
    );
  }
  if (status === 'booked_direct') {
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
        <AlertCircle size={12} /> Booked (Direct)
      </span>
    );
  }
  return null;
};

const AdminAvailability = () => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const username = localStorage.getItem('admin_username');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin/login');
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const handleSearch = async () => {
    if (!checkIn || !checkOut) {
      toast.error('Please select both check-in and check-out dates');
      return;
    }
    if (checkIn >= checkOut) {
      toast.error('Check-out must be after check-in');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/room-availability`, {
        params: { checkIn, checkOut },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRooms(res.data);
      setSearched(true);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to fetch availability');
      }
    } finally {
      setLoading(false);
    }
  };

  const available = rooms.filter(r => r.availability_status === 'available');
  const booked = rooms.filter(r => r.availability_status !== 'available');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <VedLogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Room Availability</h1>
              <p className="text-sm text-gray-600">Welcome, {username}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Link
            to="/admin/dashboard"
            className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </Link>
        </div>

        {/* Date Picker Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Check Room Availability</h2>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
              <input
                type="date"
                value={checkIn}
                onChange={e => setCheckIn(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
              <input
                type="date"
                value={checkOut}
                min={checkIn || undefined}
                onChange={e => setCheckOut(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="flex items-center gap-2 bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <Search size={16} />
              {loading ? 'Checking…' : 'Check Availability'}
            </button>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-3xl font-bold text-gray-800">{rooms.length}</p>
                <p className="text-sm text-gray-600 mt-1">Total Rooms</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{available.length}</p>
                <p className="text-sm text-gray-600 mt-1">Available</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{booked.length}</p>
                <p className="text-sm text-gray-600 mt-1">Booked</p>
              </div>
            </div>

            {/* Room Cards */}
            <div className="space-y-4">
              {rooms.map(room => (
                <div
                  key={room.id}
                  className={`bg-white rounded-xl shadow-sm border-l-4 overflow-hidden ${
                    room.availability_status === 'available'
                      ? 'border-green-500'
                      : room.availability_status === 'booked_direct'
                      ? 'border-purple-500'
                      : 'border-red-500'
                  }`}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-800">{room.name}</h3>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {room.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {room.bedType} · Max {room.maxOccupancy} guests
                        </p>
                      </div>
                      <StatusBadge
                        status={room.availability_status}
                        source={room.booking_source}
                      />
                    </div>

                    {/* Booking details */}
                    {room.availability_status !== 'available' && room.booking_info && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 text-sm space-y-1">
                        {room.availability_status === 'booked_direct' ? (
                          <>
                            <p><span className="font-medium text-gray-700">Booking Ref:</span>{' '}
                              <span className="font-mono text-orange-600">{room.booking_info.booking_ref}</span>
                            </p>
                            <p><span className="font-medium text-gray-700">Guest:</span>{' '}{room.booking_info.guest_name}</p>
                            <p><span className="font-medium text-gray-700">Phone:</span>{' '}{room.booking_info.guest_phone}</p>
                            {room.booking_info.guest_email && (
                              <p><span className="font-medium text-gray-700">Email:</span>{' '}{room.booking_info.guest_email}</p>
                            )}
                            {room.booking_info.guest_address && (
                              <p><span className="font-medium text-gray-700">Address:</span>{' '}{room.booking_info.guest_address}</p>
                            )}
                            <p><span className="font-medium text-gray-700">Dates:</span>{' '}
                              {room.booking_info.check_in} → {room.booking_info.check_out}
                            </p>
                            {room.booking_info.notes && (
                              <p><span className="font-medium text-gray-700">Notes:</span>{' '}{room.booking_info.notes}</p>
                            )}
                            <p>
                              <span className="font-medium text-gray-700">Status:</span>{' '}
                              <span className="capitalize">{room.booking_info.status}</span>
                            </p>
                          </>
                        ) : (
                          <>
                            {room.booking_info.summary && (
                              <p><span className="font-medium text-gray-700">Summary:</span>{' '}{room.booking_info.summary}</p>
                            )}
                            {room.booking_info.start_date && (
                              <p><span className="font-medium text-gray-700">Booking Dates:</span>{' '}
                                {room.booking_info.start_date} → {room.booking_info.end_date}
                              </p>
                            )}
                            {room.booking_info.description && (
                              <p><span className="font-medium text-gray-700">Details:</span>{' '}{room.booking_info.description}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              Source: {PLATFORM_LABELS[room.booking_source] || room.booking_source}
                              {' · '}Sync your iCal feeds for latest guest info.
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {rooms.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
                No rooms found.
              </div>
            )}
          </>
        )}

        {!searched && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-400">
            <Search size={48} className="mx-auto mb-3 opacity-30" />
            <p>Select dates above and click <strong>Check Availability</strong> to see room status.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAvailability;
